<script lang="ts">
  /**
   * The Now board (/now/board, also the phone Now tab): the Up next hero (the first
   * item), the "Then" queue with "Make up next", an add row while slots are open, and
   * the Done today tray. Layouts: split ≥900px, stacked 768–899px, and the phone
   * branch (`phoneLayout`: <768px wide or <500px tall; landscape puts Then beside the hero).
   */
  import { onMount, tick } from 'svelte';
  import { MediaQuery } from 'svelte/reactivity';
  import { router } from '@inertiajs/svelte';
  import type { NowBoardPollSeconds, NowItem } from '../../lib/types';
  import { NOW_BOARD_POLL_SECONDS } from '../../lib/types';
  import { formatStayDuration, stayLabel, stayNudge } from '../../lib/nowStay';
  import {
    completeNowItem,
    moveNowItemToTop,
    normalizeNowBoardPollSeconds,
    nowBoardDisplayMode,
    nowBoardKeepAwake,
    nowBoardPollSeconds,
    nowState,
    refreshNowQuietly,
    removeFromNow,
    setNowBoardKeepAwake,
    setNowBoardPollSeconds,
  } from '../../lib/stores/now';
  import { viewport } from '../../lib/stores/viewport';
  import { createScreenWakeLock, isWakeLockSupported } from '../../lib/wakeLock';
  import NowActionConfirmModal from '../../components/NowActionConfirmModal.svelte';
  import NowAddPicker from '../../components/NowAddPicker.svelte';
  import NowDoneProgress from '../../components/NowDoneProgress.svelte';
  import NowStayMeter from '../../components/NowStayMeter.svelte';
  import NowTaskDetailModal from '../../components/NowTaskDetailModal.svelte';
  import ActionSheet from '../../components/mobile/ActionSheet.svelte';

  const IDLE_MS = 2200;
  const MOVED_UP_MS = 2400;
  const POPOUT_NAME = 'momentium-now';
  const POLL_LABELS: Record<NowBoardPollSeconds, string> = {
    0: 'Off',
    30: '30s',
    60: '1 min',
    120: '2 min',
    300: '5 min',
  };

  type PendingAction = { type: 'complete' | 'remove'; item: NowItem };

  let currentNow = $derived($nowState);
  let hero = $derived<NowItem | undefined>(currentNow.items[0]);
  let queue = $derived(currentNow.items.slice(1));
  let doneCount = $derived(currentNow.completedToday.length);
  let toGo = $derived(currentNow.items.length);
  let openSlots = $derived(Math.max(0, currentNow.capacity - currentNow.occupied));
  let isEmpty = $derived(currentNow.items.length === 0 && doneCount === 0);
  let showDoneTray = $derived(!currentNow.refillSameDay && doneCount > 0);

  let chromeVisible = $state(true);
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let isPopOut = $state(false);
  let pendingAction = $state<PendingAction | null>(null);
  let nowMs = $state(Date.now());
  let settingsOpen = $state(false);
  let settingsRoot: HTMLDivElement | undefined = $state();
  let pollSeconds = $derived(normalizeNowBoardPollSeconds($nowBoardPollSeconds));
  const wakeLockSupported = isWakeLockSupported();
  // Landscape phones are wider than the mobile breakpoint; treat them as phones here too.
  const shortViewport = new MediaQuery('(max-height: 499px)');
  let phoneLayout = $derived($viewport.isMobile || shortViewport.current);
  let displayMode = $derived(wakeLockSupported && $nowBoardKeepAwake);
  let wakeLockHeld = $state(false);
  // Phones in portrait have the tab bar (Now is a tab), except in display mode.
  let showBack = $derived(!$viewport.isMobile || displayMode);

  let addOpen = $state(false);
  let emptyAddEl: HTMLButtonElement | null = $state(null);
  let addRowEl: HTMLButtonElement | null = $state(null);
  // The popover follows whichever add button is on screen: adding the first task swaps
  // the empty state's button for the add row, and the picker stays open for another add.
  let addAnchor = $derived(isEmpty ? emptyAddEl : addRowEl);
  // Closes itself when the layout or the Up next task changes (a resize past the phone
  // breakpoint unmounts the sheet; a poll can replace the hero), so it never lingers unseen.
  // A writable $derived: `heroMenuOpen = true` opens it until either input changes.
  let heroMenuOpen = $derived(closedWhenChanged(phoneLayout, hero?.taskId));
  let taskDetail: NowTaskDetailModal | undefined = $state();
  let heroTitleEl: HTMLButtonElement | undefined = $state();
  let movedUpId = $state<string | null>(null);
  let movedUpTimer: ReturnType<typeof setTimeout> | null = null;

  let localDate = $derived.by(() => {
    const [year, month, day] = currentNow.localDate.split('-').map(Number);
    if (!year || !month || !day) return new Date(nowMs);
    return new Date(year, month - 1, day);
  });
  let dateLabel = $derived(
    localDate.toLocaleDateString(
      undefined,
      phoneLayout
        ? { weekday: 'short', month: 'short', day: 'numeric' }
        : { weekday: 'long', month: 'long', day: 'numeric' },
    ),
  );
  let slotsText = $derived(`${openSlots} open ${openSlots === 1 ? 'slot' : 'slots'}`);

  /** Always false; its arguments are what a writable `$derived` resets on. */
  function closedWhenChanged(..._inputs: unknown[]): boolean {
    return false;
  }

  function stayDuration(item: NowItem, endedAt?: string): string {
    return formatStayDuration(item.addedAt, endedAt, item.currentStayDays, nowMs);
  }

  /** "11:42 AM · took 1 day 4 hours" (the time is left out when unknown). */
  function doneMeta(item: NowItem): string {
    const took = `took ${stayDuration(item, item.completedAt)}`;
    const at = item.completedAt ? new Date(item.completedAt) : null;
    if (!at || Number.isNaN(at.getTime())) return took;
    return `${at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} · ${took}`;
  }

  function pokeChrome() {
    if (!chromeVisible) chromeVisible = true;
    if (idleTimer) clearTimeout(idleTimer);
    if (settingsOpen) return;
    idleTimer = setTimeout(() => {
      chromeVisible = false;
    }, IDLE_MS);
  }

  function closeSettings() {
    if (!settingsOpen) return;
    settingsOpen = false;
    pokeChrome();
  }

  function toggleSettings() {
    if (settingsOpen) {
      closeSettings();
      return;
    }
    settingsOpen = true;
    chromeVisible = true;
    if (idleTimer) clearTimeout(idleTimer);
  }

  function goBack() {
    if (typeof window !== 'undefined' && window.name === POPOUT_NAME) {
      window.close();
      if (!window.closed) router.get('/');
      return;
    }
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
      return;
    }
    router.get('/');
  }

  function popOut() {
    window.open('/now/board', POPOUT_NAME, 'noopener,noreferrer,width=1280,height=800');
  }

  function toggleKeepAwake() {
    setNowBoardKeepAwake(!$nowBoardKeepAwake);
    pokeChrome();
  }

  /** True while something layered over the board owns Esc. */
  function overlayOpen(): boolean {
    return (
      pendingAction !== null ||
      addOpen ||
      heroMenuOpen ||
      document.querySelector('[aria-modal="true"]') !== null
    );
  }

  function handleWindowKey(event: KeyboardEvent) {
    pokeChrome();
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key === 'Escape') {
      if (settingsOpen) {
        event.preventDefault();
        closeSettings();
        return;
      }
      if (overlayOpen()) return;
      event.preventDefault();
      goBack();
    }
  }

  function requestAction(type: PendingAction['type'], item: NowItem) {
    pendingAction = { type, item };
  }

  async function confirmAction() {
    const pending = pendingAction;
    pendingAction = null;
    if (!pending) return;
    if (pending.type === 'complete') {
      await completeNowItem(pending.item.taskId, pending.item.projectId);
      return;
    }
    await removeFromNow(pending.item.taskId);
  }

  async function promote(item: NowItem) {
    if (!moveNowItemToTop(item.taskId)) return;
    movedUpId = item.taskId;
    if (movedUpTimer) clearTimeout(movedUpTimer);
    movedUpTimer = setTimeout(() => {
      movedUpId = null;
    }, MOVED_UP_MS);
    // The clicked row has left the queue; keep focus with the task in its new place.
    await tick();
    heroTitleEl?.focus();
  }

  function openTask(item: NowItem) {
    void taskDetail?.open(item);
  }

  function toggleAdd() {
    addOpen = !addOpen;
  }

  onMount(() => {
    isPopOut = window.name === POPOUT_NAME;
    pokeChrome();
    const clock = setInterval(() => {
      nowMs = Date.now();
    }, 30_000);

    let pollTimer: ReturnType<typeof setInterval> | null = null;
    const unsubPoll = nowBoardPollSeconds.subscribe((value) => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
      const seconds = normalizeNowBoardPollSeconds(value);
      if (seconds === 0) return;
      pollTimer = setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        refreshNowQuietly();
      }, seconds * 1000);
    });

    function onVisibility() {
      if (document.visibilityState === 'visible') {
        nowMs = Date.now();
        refreshNowQuietly();
      }
    }
    document.addEventListener('visibilitychange', onVisibility);

    const wakeLock = createScreenWakeLock((held) => {
      wakeLockHeld = held;
    });
    const unsubKeepAwake = nowBoardKeepAwake.subscribe((on) => {
      const active = wakeLockSupported && on;
      nowBoardDisplayMode.set(active);
      if (active) wakeLock.enable();
      else wakeLock.disable();
    });

    return () => {
      clearInterval(clock);
      if (pollTimer) clearInterval(pollTimer);
      unsubPoll();
      document.removeEventListener('visibilitychange', onVisibility);
      if (idleTimer) clearTimeout(idleTimer);
      if (movedUpTimer) clearTimeout(movedUpTimer);
      unsubKeepAwake();
      wakeLock.disable();
      nowBoardDisplayMode.set(false);
    };
  });

  // eslint-disable-next-line momentium/no-unguarded-effect -- document listener while the settings popover is open
  $effect(() => {
    if (!settingsOpen) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (settingsRoot?.contains(target)) return;
      closeSettings();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  });
</script>

{#snippet checkIcon(size: number)}
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="m3.5 8.5 3 3 6-7"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
{/snippet}

{#snippet promoteIcon(size: number)}
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M8 14V6M4.5 9.5 8 6l3.5 3.5M3 2.5h10"
      stroke="currentColor"
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
{/snippet}

{#snippet plusIcon()}
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
  </svg>
{/snippet}

<svelte:head><title>Now · Momentium</title></svelte:head>

<svelte:window onpointermove={pokeChrome} onkeydown={handleWindowKey} ontouchstart={pokeChrome} />

<div
  class="now-board"
  class:phone={phoneLayout}
  class:short={phoneLayout && shortViewport.current}
  class:display-mode={displayMode}
  class:awake={wakeLockHeld}
  data-now-board
>
  <header class="board-top" class:chrome-visible={chromeVisible || settingsOpen}>
    <div class="top-left">
      {#if showBack}
        <button
          type="button"
          class="chrome chrome-btn back-btn"
          aria-label={phoneLayout ? 'Back' : undefined}
          onclick={goBack}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M10 3 5 8l5 5"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {#if !phoneLayout}Back{/if}
        </button>
      {/if}
      <div class="title-block">
        <h1 class="page-title">Now</h1>
        <span class="page-date"
          >{dateLabel}{#if phoneLayout && toGo > 0}&nbsp;· {toGo} to go{/if}</span
        >
      </div>
    </div>

    <div class="top-right">
      {#if !(phoneLayout && currentNow.refillSameDay)}
        <div class="progress">
          <NowDoneProgress nowState={currentNow} size="sm" />
          {#if !phoneLayout && !currentNow.refillSameDay && !isEmpty}
            <div class="progress-text">
              <span class="progress-done">{doneCount} done today</span>
              <span class="progress-rest"
                >{toGo > 0 ? `${toGo} to go` : 'All done'}{#if openSlots > 0}&nbsp;· {slotsText}{/if}</span
              >
            </div>
          {/if}
        </div>
      {/if}
      {#if phoneLayout}
        {#if wakeLockSupported}
          <button
            type="button"
            class="chrome chrome-btn icon"
            aria-label="Keep awake"
            aria-pressed={displayMode}
            title="Keep the screen on while this board is open"
            onclick={toggleKeepAwake}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.8" />
              <path
                d="M12 2.5v2M12 19.5v2M4.6 4.6 6 6M18 18l1.4 1.4M2.5 12h2M19.5 12h2M4.6 19.4 6 18M18 6l1.4-1.4"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </button>
        {/if}
      {:else if !isPopOut}
        <button
          type="button"
          class="chrome chrome-btn icon"
          aria-label="Pop out"
          title="Pop out the board into its own window"
          onclick={popOut}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M9.25 3H13v3.75M13 3 8.75 7.25M6.75 13H3V9.25M3 13l4.25-4.25"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      {/if}

      <div class="chrome settings-root" bind:this={settingsRoot}>
        <button
          type="button"
          class="chrome-btn icon"
          class:active={settingsOpen}
          title="Board settings"
          aria-label="Board settings"
          aria-haspopup="true"
          aria-expanded={settingsOpen}
          onclick={toggleSettings}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"
              stroke="currentColor"
              stroke-width="2"
              stroke-linejoin="round"
            />
            <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" />
          </svg>
        </button>
        {#if settingsOpen}
          <div class="settings-popover" role="dialog" aria-label="Board settings">
            <p class="settings-label">Refresh cards</p>
            <div class="poll-pills" role="radiogroup" aria-label="Refresh interval">
              {#each NOW_BOARD_POLL_SECONDS as option}
                <button
                  type="button"
                  class="poll-pill"
                  class:active={pollSeconds === option}
                  onclick={() => setNowBoardPollSeconds(option)}
                >
                  {POLL_LABELS[option]}
                </button>
              {/each}
            </div>
            <p class="settings-hint">Updates Now in place. Off disables automatic refresh.</p>
            {#if wakeLockSupported && !phoneLayout}
              <label class="settings-switch">
                <input
                  type="checkbox"
                  role="switch"
                  checked={displayMode}
                  onchange={(event) => setNowBoardKeepAwake(event.currentTarget.checked)}
                />
                Keep screen awake
              </label>
              <p class="settings-hint">
                Stops the screen dimming or locking while the board is open.
              </p>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  </header>

  {#if phoneLayout && currentNow.refillSameDay && !isEmpty}
    <div class="refill-line"><NowDoneProgress nowState={currentNow} /></div>
  {/if}

  <main class="board-body" class:is-empty={isEmpty}>
    {#if isEmpty}
      <section class="empty-state" aria-label="Now is empty">
        <h2 class="empty-title">Nothing in Now yet</h2>
        <p class="empty-copy">
          Pick up to {currentNow.capacity}
          {currentNow.capacity === 1 ? 'task' : 'tasks'} to commit to today. The first one is up next.
        </p>
        {#if currentNow.canAdd}
          <button
            type="button"
            class="empty-add"
            aria-haspopup="dialog"
            aria-expanded={addOpen}
            bind:this={emptyAddEl}
            onclick={toggleAdd}
          >
            {@render plusIcon()}
            <span class="add-label">Add a task</span>
          </button>
        {/if}
      </section>
    {:else}
      {#if hero}
        <section
          class="hero"
          class:moved-up={movedUpId === hero.taskId}
          aria-label="Up next"
          data-hero
        >
          <div class="hero-main">
            <div class="hero-kicker-row">
              <span class="hero-kicker">Up next</span>
              {#if movedUpId === hero.taskId}
                <span class="moved-tag">Moved up</span>
              {/if}
              {#if phoneLayout}
                <button
                  type="button"
                  class="hero-more"
                  aria-label="More actions for {hero.title}"
                  aria-haspopup="menu"
                  onclick={() => (heroMenuOpen = true)}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <circle cx="5.5" cy="12" r="1.8" />
                    <circle cx="12" cy="12" r="1.8" />
                    <circle cx="18.5" cy="12" r="1.8" />
                  </svg>
                </button>
              {/if}
            </div>
            <h2 class="hero-title">
              <button
                type="button"
                class="title-btn"
                bind:this={heroTitleEl}
                title={hero.title}
                onclick={() => hero && openTask(hero)}
                ><span class="title-text">{hero.title}</span></button
              >
            </h2>
            <span class="hero-project">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                aria-hidden="true"
              >
                <path
                  d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
                />
              </svg>
              {hero.projectTitle}
            </span>
            <div class="hero-stay">
              <NowStayMeter
                days={hero.currentStayDays}
                variant="onDark"
                size={phoneLayout ? 'sm' : 'lg'}
                label={stayLabel(hero, nowMs)}
              />
              {#if stayNudge(hero.currentStayDays)}
                <span class="hero-nudge">{stayNudge(hero.currentStayDays)}</span>
              {/if}
            </div>
          </div>

          {#if phoneLayout}
            <div class="hero-check-col">
              <button
                type="button"
                class="hero-check"
                aria-label="Mark {hero.title} done"
                onclick={() => hero && requestAction('complete', hero)}
              >
                {@render checkIcon(24)}
              </button>
              <span class="hero-check-label" aria-hidden="true">Done</span>
            </div>
          {:else}
            <div class="hero-actions">
              <button
                type="button"
                class="hero-done"
                onclick={() => hero && requestAction('complete', hero)}
              >
                {@render checkIcon(20)}Mark done
              </button>
              <button type="button" class="hero-open" onclick={() => hero && openTask(hero)}>
                Open task
              </button>
              <span class="hero-actions-gap"></span>
              <button
                type="button"
                class="hero-not-today"
                onclick={() => hero && requestAction('remove', hero)}
              >
                Not today
              </button>
            </div>
          {/if}
        </section>
      {:else}
        <section class="hero all-done" aria-label="Up next" data-hero>
          <div class="hero-main">
            <span class="hero-kicker">Today</span>
            <h2 class="hero-title">All done for today</h2>
            <p class="hero-sub">
              {#if openSlots > 0}
                Nice work. There's room for {openSlots} more if you want it.
              {:else}
                Nice work. Your slots open up again tomorrow.
              {/if}
            </p>
          </div>
        </section>
      {/if}

      <div class="queue">
        {#if hero}
          <section class="then" aria-label="Then">
            <div class="queue-head">
              <h2 class="section-label">Then</h2>
              {#if queue.length > 0}
                <span class="queue-hint">{@render promoteIcon(12)} makes it Up next</span>
              {/if}
            </div>
            {#if queue.length === 0}
              <p class="queue-empty">Nothing else in Now</p>
            {:else}
              <ul class="then-list">
                {#each queue as item (item.taskId)}
                  <li class="then-row">
                    <div class="row-main">
                      <h3 class="row-title">
                        <button
                          type="button"
                          class="title-btn"
                          title={item.title}
                          onclick={() => openTask(item)}
                          ><span class="title-text">{item.title}</span></button
                        >
                      </h3>
                      <div class="row-meta">
                        <NowStayMeter days={item.currentStayDays} label={stayLabel(item, nowMs)} />
                        <span class="row-project">· {item.projectTitle}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="promote-btn"
                      aria-label="Make {item.title} up next"
                      onclick={() => promote(item)}
                    >
                      <span class="promote-face">
                        {@render promoteIcon(phoneLayout ? 20 : 16)}
                        <span class="promote-label" aria-hidden="true">Make up next</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      class="row-check"
                      aria-label="Mark {item.title} done"
                      onclick={() => requestAction('complete', item)}
                    >
                      {@render checkIcon(phoneLayout ? 20 : 22)}
                    </button>
                  </li>
                {/each}
              </ul>
            {/if}
          </section>
        {/if}

        {#if openSlots > 0}
          <button
            type="button"
            class="add-row"
            aria-haspopup="dialog"
            aria-expanded={addOpen}
            bind:this={addRowEl}
            onclick={toggleAdd}
          >
            {@render plusIcon()}
            <span class="add-label">Add a task</span>
            <span class="add-detail"
              >{phoneLayout ? `· ${slotsText}` : `${slotsText} left today`}</span
            >
          </button>
        {/if}

        {#if showDoneTray}
          <section class="done-tray" aria-label="Done today">
            <div class="done-head">
              <h2 class="section-label done-label">Done today</h2>
              {#if !phoneLayout}
                <span class="done-hint">Slots stay used until tomorrow</span>
              {/if}
            </div>
            <ul class="done-list">
              {#each currentNow.completedToday as item (item.taskId)}
                <li class="done-row">
                  <span class="done-dot">{@render checkIcon(13)}</span>
                  <span class="done-title" title={item.title}>{item.title}</span>
                  <span class="done-meta">{doneMeta(item)}</span>
                </li>
              {/each}
            </ul>
          </section>
        {/if}
      </div>
    {/if}
  </main>

  {#if wakeLockHeld}
    <p class="awake-indicator" role="status" title="The screen stays on while this board is open">
      <span aria-hidden="true">☀︎</span> Screen awake
    </p>
  {/if}
</div>

<NowAddPicker
  open={addOpen}
  mode={phoneLayout ? 'sheet' : 'popover'}
  anchor={phoneLayout ? null : addAnchor}
  onClose={() => (addOpen = false)}
/>

{#if phoneLayout}
  <ActionSheet
    open={heroMenuOpen}
    onClose={() => (heroMenuOpen = false)}
    title={hero?.title}
    label="Up next actions"
    actions={hero
      ? [
          { label: 'Open task', icon: 'open', onSelect: () => hero && openTask(hero) },
          {
            label: 'Not today',
            icon: 'close',
            danger: true,
            onSelect: () => hero && requestAction('remove', hero),
          },
        ]
      : []}
  />
{/if}

<NowTaskDetailModal bind:this={taskDetail} />

<NowActionConfirmModal
  open={pendingAction !== null}
  action={pendingAction?.type ?? 'complete'}
  taskTitle={pendingAction?.item.title ?? ''}
  refillSameDay={currentNow.refillSameDay}
  onCancel={() => (pendingAction = null)}
  onConfirm={confirmAction}
/>

<style>
  /* Tokens: epic ma-t2tr "Design tokens". Type sizes are variables so the touch
     bump and the phone branch only swap values. */
  .now-board {
    --page-bg: #f4f3f0;
    --surface: #ffffff;
    --border: #e6e3dd;
    --ink: #141210;
    --ink-2: #5a544d;
    --ink-3: #6b655d;
    --hero-bg: #1a1612;
    --hero-text: #ffffff;
    --hero-text-2: #c9c2b9;
    --hero-kicker: #fbbf24;
    --done-bg: #22c55e;
    --done-ink: #052e16;
    --done-outline: #15803d;
    --tray-bg: #eaf6ee;
    --tray-ink: #14532d;
    --tray-ink-2: #2f6b40;

    --pad-x: 2.5rem;
    --gap: 1.5rem;
    --page-title: 22px;
    --hero-title: clamp(2.25rem, 5.6vw - 1.3rem, 3.75rem);
    --hero-kicker-size: 14px;
    --hero-meta: 17px;
    --hero-btn: 18px;
    --row-title: 22px;
    --meta: 14px;
    --label: 13px;
    --check: 52px;
    --promote: 44px;

    position: relative;
    flex: 1;
    min-height: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--page-bg);
    color: var(--ink);
    overflow: auto;
    -webkit-font-smoothing: antialiased;
  }

  /* iPad and other touch screens ≥768px: read at arm's length (epic decision 11). */
  @media (pointer: coarse) and (min-width: 768px) {
    .now-board:not(.phone) {
      --page-title: 26px;
      --hero-title: clamp(2.875rem, 4.4vw, 4.125rem);
      --hero-kicker-size: 15px;
      --hero-meta: 19px;
      --hero-btn: 20px;
      --row-title: 24px;
      --meta: 16px;
      --label: 14px;
      --check: 56px;
      --promote: 48px;
    }
  }

  button {
    font: inherit;
  }

  /* ---------- Header ---------- */

  .board-top {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.75rem var(--pad-x) 0;
  }

  .top-left,
  .top-right {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    min-width: 0;
  }

  .top-left {
    gap: 1.125rem;
  }

  .title-block {
    display: flex;
    align-items: baseline;
    gap: 1.125rem;
    min-width: 0;
  }

  .page-title {
    margin: 0;
    font-size: var(--page-title);
    font-weight: 800;
    letter-spacing: -0.01em;
  }

  .page-date {
    font-size: 15px;
    color: var(--ink-3);
    white-space: nowrap;
  }

  .progress {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    margin-right: 0.625rem;
  }

  .progress-text {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
  }

  .progress-done {
    font-size: 15px;
    font-weight: 600;
  }

  .progress-rest {
    font-size: 13px;
    color: var(--ink-3);
  }

  /* Chrome auto-hide: buttons fade after inactivity; the title and ring stay. */
  .chrome {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
  }

  .board-top.chrome-visible .chrome {
    opacity: 1;
    pointer-events: auto;
  }

  /* Touch keeps chrome visible, except in display mode where it fades after inactivity. */
  @media (hover: none), (pointer: coarse) {
    .now-board:not(.display-mode) .chrome {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .chrome-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    height: 44px;
    padding: 0 1rem;
    border-radius: 999px;
    border: 1px solid #dedbd5;
    background: var(--surface);
    color: #3b3732;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
  }

  .chrome-btn:hover,
  .chrome-btn.active {
    background: #faf9f7;
    border-color: #cfcac2;
  }

  .chrome-btn.icon {
    width: 44px;
    padding: 0;
    color: #4a4540;
  }

  .chrome-btn[aria-pressed='true'] {
    background: #fef3c7;
    border-color: #f59e0b;
    color: #78350f;
  }

  .settings-root {
    position: relative;
  }

  .settings-popover {
    position: absolute;
    top: calc(100% + 0.5rem);
    right: 0;
    z-index: 30;
    width: 16.5rem;
    padding: 0.75rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 0.875rem;
    box-shadow: 0 12px 28px rgba(26, 22, 18, 0.12);
  }

  .settings-label {
    margin: 0 0 0.45rem;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-3);
  }

  .poll-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
  }

  .poll-pill {
    border: 1px solid var(--border);
    background: var(--page-bg);
    color: var(--ink-2);
    border-radius: 999px;
    padding: 0.2rem 0.55rem;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
  }

  /* Touch: full-size tap targets (phones, and iPads in the split layout). */
  @media (pointer: coarse) {
    .poll-pill {
      min-width: 2.75rem;
      min-height: 2.75rem;
    }

    .settings-switch {
      min-height: 2.75rem;
    }
  }

  .phone .poll-pill {
    min-width: 2.75rem;
    min-height: 2.75rem;
    padding-inline: 0.55rem;
  }

  .poll-pill.active {
    background: var(--hero-bg);
    border-color: var(--hero-bg);
    color: #fff;
  }

  .settings-hint {
    margin: 0.5rem 0 0;
    font-size: 0.75rem;
    line-height: 1.4;
    color: var(--ink-3);
  }

  .settings-switch {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.7rem;
    padding-top: 0.65rem;
    border-top: 1px solid var(--border);
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--ink);
    cursor: pointer;
  }

  .settings-switch input {
    width: 1rem;
    height: 1rem;
    margin: 0;
    accent-color: #d97706;
    cursor: pointer;
  }

  .awake-indicator {
    position: fixed;
    right: calc(0.85rem + var(--safe-right, 0px));
    bottom: calc(0.6rem + var(--safe-bottom, 0px));
    z-index: 10;
    margin: 0;
    padding: 0.15rem 0.55rem;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.85);
    color: var(--ink-3);
    font-size: 0.75rem;
    font-weight: 600;
    pointer-events: none;
  }

  .now-board.awake .board-body {
    padding-bottom: 2.25rem;
  }

  /* ---------- Body: split (≥900px) by default, stacked below ---------- */

  .board-body {
    flex: 1;
    min-height: 0;
    width: 100%;
    max-width: 100rem;
    margin: 0 auto;
    padding: 1.5rem var(--pad-x) 2.25rem;
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
    grid-template-rows: minmax(min-content, 1fr);
    gap: var(--gap);
  }

  .board-body.is-empty {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Stacked tablet: the hero spans the width, the queue follows; the page scrolls. */
  @media (max-width: 899px) {
    .now-board:not(.phone) .board-body {
      display: flex;
      flex-direction: column;
      flex: none;
      min-height: auto;
    }

    .now-board:not(.phone) .queue {
      overflow: visible;
    }
  }

  /* ---------- Hero ---------- */

  .hero {
    display: flex;
    flex-direction: column;
    gap: 1.625rem;
    min-width: 0;
    padding: 3rem 3.25rem 2.75rem;
    border-radius: 2rem;
    background: var(--hero-bg);
    color: var(--hero-text);
    transition: box-shadow 0.25s ease;
  }

  .hero.moved-up {
    box-shadow: 0 0 0 3px var(--hero-kicker);
  }

  .hero-main {
    display: flex;
    flex-direction: column;
    gap: 1.625rem;
    min-width: 0;
  }

  .hero-kicker-row {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    min-height: 1.5rem;
  }

  .hero-kicker {
    font-size: var(--hero-kicker-size);
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--hero-kicker);
  }

  .moved-tag {
    display: inline-flex;
    align-items: center;
    height: 1.375rem;
    padding: 0 0.625rem;
    border-radius: 999px;
    background: rgba(251, 191, 36, 0.16);
    color: #fde68a;
    font-size: 12px;
    font-weight: 600;
    animation: moved-in 0.25s ease-out;
  }

  @keyframes moved-in {
    from {
      opacity: 0;
      transform: translateY(2px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .hero {
      transition: none;
    }

    .moved-tag {
      animation: none;
    }
  }

  .hero-title {
    margin: 0;
    max-width: 13em;
    font-size: var(--hero-title);
    line-height: 1.06;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  .title-btn {
    position: relative;
    display: block;
    max-width: 100%;
    margin: 0;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    letter-spacing: inherit;
    line-height: inherit;
    text-align: left;
    cursor: pointer;
  }

  .title-text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    overflow: hidden;
    overflow-wrap: anywhere;
  }

  /* A one-line title is shorter than a 44px tap target: grow the hit area (not the text)
     to at least 44px tall, centred on the title. Nothing interactive sits within reach
     except the phone hero's ⋯, which stays on top. */
  .title-btn::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: max(100%, 44px);
    transform: translateY(-50%);
  }

  .title-btn:hover {
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 0.12em;
  }

  .title-btn:focus-visible {
    outline: 3px solid var(--hero-kicker);
    outline-offset: 4px;
    border-radius: 4px;
  }

  .hero-project {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    font-size: var(--hero-meta);
    color: var(--hero-text-2);
  }

  .hero-stay {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    font-size: calc(var(--hero-meta) + 1px);
  }

  .hero-nudge {
    font-style: italic;
    color: var(--hero-text-2);
  }

  .hero-sub {
    margin: 0;
    font-size: var(--hero-meta);
    line-height: 1.45;
    color: var(--hero-text-2);
  }

  /* Nothing left to show, so in the split layout the card hugs its content instead of
     filling the column. */
  @media (min-width: 900px) {
    .now-board:not(.phone) .hero.all-done {
      align-self: start;
    }
  }

  .hero.all-done .hero-title {
    font-size: calc(var(--hero-title) * 0.8);
  }

  .hero-actions {
    margin-top: auto;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
  }

  .hero-actions-gap {
    flex: 1;
  }

  .hero-done,
  .hero-open,
  .hero-not-today {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    height: 3.5rem;
    border-radius: 1rem;
    font-size: var(--hero-btn);
    cursor: pointer;
  }

  .hero-done {
    padding: 0 1.75rem;
    border: 0;
    background: var(--done-bg);
    color: var(--done-ink);
    font-weight: 700;
  }

  .hero-done:hover {
    background: #4ade80;
  }

  .hero-open {
    padding: 0 1.375rem;
    border: 1px solid #4a443d;
    background: transparent;
    color: #f5f1ec;
    font-weight: 500;
  }

  .hero-open:hover {
    border-color: #6b645c;
    background: rgba(255, 255, 255, 0.05);
  }

  .hero-not-today {
    padding: 0 0.75rem;
    border: 0;
    background: transparent;
    color: #b5aea5;
    font-size: calc(var(--hero-btn) - 2px);
    text-decoration: underline;
    text-underline-offset: 4px;
  }

  .hero-not-today:hover {
    color: #f5f1ec;
  }

  .hero :focus-visible {
    outline: 3px solid var(--hero-kicker);
    outline-offset: 3px;
  }

  /* ---------- Then queue ---------- */

  .queue {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
    min-width: 0;
    min-height: 0;
    overflow-y: auto;
  }

  .then {
    display: flex;
    flex-direction: column;
    gap: inherit;
  }

  .queue-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .section-label {
    margin: 0;
    font-size: var(--label);
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-3);
  }

  .queue-hint {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 13px;
    color: var(--ink-3);
  }

  .queue-empty {
    margin: 0;
    padding: 1.125rem 1.25rem;
    border-radius: 1.25rem;
    border: 1px dashed #d3cfc8;
    color: var(--ink-3);
    font-size: calc(var(--meta) + 1px);
  }

  .then-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
  }

  .then-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) var(--promote) var(--check);
    grid-template-areas: 'main promote check';
    gap: 0.875rem;
    align-items: center;
    padding: 1.125rem 1.125rem 1.125rem 1.375rem;
    border-radius: 1.25rem;
    background: var(--surface);
    border: 1px solid var(--border);
  }

  .row-main {
    grid-area: main;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    min-width: 0;
  }

  .row-title {
    margin: 0;
    font-size: var(--row-title);
    line-height: 1.22;
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  .row-title .title-text {
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  .row-title .title-btn:focus-visible {
    outline-color: var(--ink);
  }

  .row-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem 0.625rem;
    min-width: 0;
    font-size: var(--meta);
    color: var(--ink-2);
  }

  .row-project {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* The button keeps a fixed slot in the row; its face is anchored to the right and
     grows leftward over the title on hover/focus to reveal the label, so the row never
     reflows. */
  .promote-btn {
    grid-area: promote;
    position: relative;
    width: var(--promote);
    height: var(--promote);
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
  }

  .promote-face {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 100%;
    height: 100%;
    padding: 0 calc((var(--promote) - 1rem) / 2 - 1px);
    border-radius: 0.75rem;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--ink-3);
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    transition:
      background 0.15s ease,
      color 0.15s ease,
      box-shadow 0.15s ease;
  }

  /* Icon-only until hover or keyboard focus reveals the label. */
  .promote-label {
    display: inline-block;
    max-width: 0;
    margin-left: 0;
    overflow: hidden;
    opacity: 0;
    transition:
      max-width 0.18s ease,
      margin-left 0.18s ease,
      opacity 0.12s ease;
  }

  .promote-btn:hover .promote-face,
  .promote-btn:focus-visible .promote-face {
    border-color: var(--hero-bg);
    background: var(--hero-bg);
    color: #fff;
    box-shadow:
      -10px 0 12px -2px var(--surface),
      0 6px 16px rgba(26, 22, 18, 0.18);
  }

  .promote-btn:hover .promote-label,
  .promote-btn:focus-visible .promote-label {
    max-width: 8rem;
    margin-left: 0.5rem;
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    .promote-face,
    .promote-label {
      transition: none;
    }
  }

  .promote-btn:focus-visible {
    outline: none;
  }

  .promote-btn:focus-visible .promote-face {
    outline: 3px solid #d97706;
    outline-offset: 2px;
  }

  .row-check:focus-visible,
  .add-row:focus-visible,
  .empty-add:focus-visible {
    outline: 3px solid #d97706;
    outline-offset: 2px;
  }

  .row-check {
    grid-area: check;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--check);
    height: var(--check);
    border-radius: 50%;
    border: 2px solid var(--done-outline);
    background: var(--surface);
    color: var(--done-outline);
    cursor: pointer;
  }

  .row-check:hover {
    background: var(--done-outline);
    color: #fff;
  }

  /* ---------- Add row / empty state ---------- */

  .add-row,
  .empty-add {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    min-height: 4rem;
    padding: 0 1.375rem;
    border-radius: 1.25rem;
    border: 2px dashed #d3cfc8;
    background: transparent;
    color: #3b3732;
    text-align: left;
    cursor: pointer;
  }

  .add-row:hover,
  .add-row[aria-expanded='true'],
  .empty-add:hover {
    border-color: #a8a29a;
    background: rgba(255, 255, 255, 0.5);
  }

  .add-label {
    font-size: calc(var(--meta) + 3px);
    font-weight: 600;
  }

  .add-detail {
    font-size: calc(var(--meta) + 1px);
    color: var(--ink-3);
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.875rem;
    max-width: 28rem;
    text-align: center;
  }

  .empty-title {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 800;
    letter-spacing: -0.015em;
  }

  .empty-copy {
    margin: 0 0 0.5rem;
    font-size: 1.0625rem;
    line-height: 1.5;
    color: var(--ink-2);
  }

  .empty-add {
    justify-content: center;
    min-width: 14rem;
  }

  /* ---------- Done today ---------- */

  .done-tray {
    container-type: inline-size;
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    padding: 1.125rem 1.25rem;
    border-radius: 1.25rem;
    background: var(--tray-bg);
  }

  .done-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .done-label {
    color: var(--tray-ink-2);
  }

  .done-hint {
    font-size: 13px;
    color: var(--tray-ink-2);
  }

  .done-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .done-row {
    display: grid;
    grid-template-columns: 1.625rem minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.75rem;
  }

  .done-dot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.625rem;
    height: 1.625rem;
    border-radius: 50%;
    background: #16a34a;
    color: #fff;
  }

  .done-title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: calc(var(--meta) + 3px);
    color: var(--tray-ink);
  }

  .done-meta {
    font-size: var(--meta);
    color: var(--tray-ink-2);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  /* Narrow trays (phones, the iPad split column): the meta goes under the title. */
  @container (max-width: 30rem) {
    .done-row {
      grid-template-columns: 1.5rem minmax(0, 1fr);
      grid-template-areas:
        'dot title'
        'dot meta';
      column-gap: 0.75rem;
      row-gap: 1px;
    }

    .done-dot {
      grid-area: dot;
      width: 1.5rem;
      height: 1.5rem;
    }

    .done-title {
      grid-area: title;
    }

    .done-meta {
      grid-area: meta;
      font-size: calc(var(--meta) - 1px);
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .hero-more,
  .hero-check-col,
  .refill-line {
    display: none;
  }

  /* ---------- Stacked tablet sizing ---------- */

  @media (max-width: 899px) {
    .now-board:not(.phone) {
      --pad-x: 1.75rem;
    }

    .now-board:not(.phone) .hero {
      padding: 2.25rem 2.25rem 2rem;
      border-radius: 1.75rem;
    }

    .now-board:not(.phone) .hero-title {
      max-width: none;
    }

    .now-board:not(.phone) .done-tray {
      margin-top: 0.5rem;
    }
  }

  /* ---------- Phone (ma-b4vh): <768px wide or <500px tall ---------- */

  .now-board.phone {
    --pad-x: 1rem;
    --gap: 0.75rem;
    --page-title: 28px;
    --hero-title: 24px;
    --hero-kicker-size: 12px;
    --hero-meta: 14px;
    --row-title: 17px;
    --meta: 13px;
    --label: 12px;
    --check: 48px;
    --promote: 44px;
  }

  .phone .board-top {
    padding: calc(0.875rem + var(--safe-top, 0px)) calc(var(--pad-x) + var(--safe-right, 0px)) 0
      calc(var(--pad-x) + var(--safe-left, 0px));
    gap: 0.5rem;
  }

  .phone .top-left {
    gap: 0.5rem;
  }

  .phone .top-right {
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .phone .title-block {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }

  .phone .page-title {
    letter-spacing: -0.02em;
    line-height: 1.1;
  }

  .phone .page-date {
    font-size: 13px;
  }

  .phone .progress {
    margin-right: 0;
  }

  .phone .refill-line {
    display: block;
    padding: 0.5rem var(--pad-x) 0;
  }

  .phone .board-body {
    display: flex;
    flex-direction: column;
    flex: none;
    min-height: auto;
    padding: 0.75rem var(--pad-x) 1rem;
  }

  .phone .board-body.is-empty {
    flex: 1;
  }

  .phone .hero {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 56px;
    gap: 0.875rem;
    align-items: start;
    padding: 1.125rem 1rem 1.125rem 1.25rem;
    border-radius: 1.375rem;
  }

  .phone .hero.all-done {
    display: flex;
  }

  .phone .hero-main {
    gap: 0.625rem;
  }

  .phone .hero-kicker-row {
    min-height: 2.75rem;
    margin: -0.75rem 0 -0.5rem;
  }

  .phone .hero-more {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    margin-left: auto;
    margin-right: -0.5rem;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--hero-text-2);
    cursor: pointer;
  }

  .phone .hero-title {
    line-height: 1.15;
    letter-spacing: -0.015em;
  }

  .phone .hero-stay {
    flex-direction: column;
    gap: 0.375rem;
    font-size: 13px;
  }

  .phone .hero-nudge {
    font-size: 13px;
  }

  .phone .hero-check-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
    padding-top: 1.375rem;
  }

  .hero-check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: 0;
    background: var(--done-bg);
    color: var(--done-ink);
    cursor: pointer;
  }

  .hero-check-label {
    font-size: 11px;
    font-weight: 600;
    color: #86efac;
  }

  .phone .queue {
    gap: 0.625rem;
    overflow: visible;
  }

  .phone .queue-head {
    margin-top: 0.125rem;
  }

  .phone .queue-hint {
    font-size: 12px;
  }

  .phone .then-list {
    gap: 0.625rem;
  }

  .phone .then-row {
    grid-template-columns: 44px minmax(0, 1fr) 48px;
    grid-template-areas: 'promote main check';
    gap: 0.625rem;
    padding: 0.75rem 0.75rem 0.75rem 0.5rem;
    border-radius: 1.125rem;
  }

  .phone .row-main {
    gap: 0.375rem;
  }

  .phone .row-title {
    line-height: 1.25;
  }

  .phone .row-project {
    display: none;
  }

  .phone .promote-face {
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #8a8379;
  }

  .phone .promote-btn:hover .promote-face,
  .phone .promote-btn:focus-visible .promote-face {
    background: #f4f3f0;
    color: var(--ink);
    box-shadow: none;
  }

  .phone .promote-label,
  .phone .promote-btn:hover .promote-label,
  .phone .promote-btn:focus-visible .promote-label {
    display: none;
  }

  .phone .add-row {
    min-height: 3.5rem;
    padding: 0 1rem;
    border-radius: 1.125rem;
    gap: 0.75rem;
  }

  .phone .add-label {
    font-size: 15px;
  }

  .phone .add-detail {
    font-size: 14px;
  }

  .phone .done-tray {
    margin-top: 0.375rem;
    padding: 0.75rem 1rem;
    border-radius: 1.125rem;
  }

  .phone .done-title {
    font-size: 15px;
  }

  .phone .empty-title {
    font-size: 1.5rem;
  }

  .phone .empty-add {
    min-height: 3.5rem;
  }

  /* Landscape phone: hero on the left, Then on the right, each column scrolls. */
  .now-board.phone.short {
    --page-title: 22px;
    overflow: hidden;
  }

  .phone.short .board-top {
    padding-top: calc(0.5rem + var(--safe-top, 0px));
  }

  .phone.short .title-block {
    flex-direction: row;
    align-items: baseline;
    gap: 0.75rem;
  }

  .phone.short .board-body:not(.is-empty) {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr);
    align-items: start;
    padding-top: 0.5rem;
    padding-bottom: 0.75rem;
  }

  .phone.short .hero,
  .phone.short .queue {
    max-height: 100%;
    overflow-y: auto;
  }

  .phone.short .hero-title {
    font-size: 22px;
  }
</style>
